import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { commandsFor, type Command, type Places, type Verbs } from '../palette';
import { howTosFor, HOW_TO } from '../help';
import { canCreateIssues, type IssueSource } from '../ownership';
import { can } from '../permissions';
import { MENU_MS, useDismiss, useFocusTrap } from '../motion';
import { setThemeMode, type ThemeMode } from '../theme';
import {
  STATUS_LABELS,
  PRIORITY_ORDER,
  PRIORITY_LABELS,
  type IssueStatus,
  type IssuePriority,
} from '../types';
import { statusOrderFor } from '../lifecycle';
import Icon from './Icon';

/** One how-to → a `help` Command. Shared by the filtered list and the full index.
 *  ⌘K is the quick jump, so it coaches and drills to the PRIMARY way (ways[0]);
 *  when an entry has more than one way, the coach-mark says so and the full
 *  step-by-step for every way lives on the Help screen. */
const toHelpCmd = (h: (typeof HOW_TO)[number]): Command => {
  const w = h.ways[0];
  const more = h.ways.length - 1;
  return {
    kind: 'help',
    id: h.id,
    label: h.q,
    hint: h.group,
    view: w.target?.view ?? 'board',
    reveal: w.target?.reveal,
    answer: more > 0 ? `${w.steps}  (+${more} other way${more > 1 ? 's' : ''} in Help)` : w.steps,
  };
};

/**
 * Everything reachable, from the keyboard.
 *
 * WHY THE SELECTION IS AN INDEX AND NOT AN ID
 *
 * The list changes on every keystroke. Holding the chosen COMMAND would mean
 * deciding, each time, whether the thing you had highlighted still exists — and
 * if it does not, which of the new results inherits it. Holding the position
 * makes that question disappear: the first result is always the one meant,
 * because typing more is how you say "not that one".
 *
 * It is clamped rather than reset, so arrowing down and then typing another
 * letter does not silently jump back to the top of a list you had moved past.
 */
export default function Palette({
  places,
  close,
  // 'all' is the ⌘K palette (go anywhere, find a card, run a command). 'help' is
  // the Help screen: the SAME surface in "how do I…" mode — an empty query lists
  // every how-to as the index, and typing filters them. One surface, two doors
  // (design rule P9). Defaults to 'all' so ⌘K callers are unchanged.
  mode = 'all',
}: {
  places: Places[];
  close: () => void;
  mode?: 'all' | 'help';
}) {
  const issues = useApp((s) => s.issues);
  const role = useApp((s) => s.shownRole);
  const source = useApp((s) => (s.meta.source ?? 'tracker') as IssueSource);
  const stagingOn = useApp((s) => s.meta.stagingEnabled === true);
  const setView = useApp((s) => s.setView);
  const openIssue = useApp((s) => s.openIssue);
  const startCreate = useApp((s) => s.startCreate);
  const selectedId = useApp((s) => s.selectedId);
  const updateIssue = useApp((s) => s.updateIssue);
  const signOutUser = useApp((s) => s.signOutUser);
  const myEmail = (useApp((s) => s.user?.email) ?? '').toLowerCase();
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const setProject = useApp((s) => s.setProject);
  const issueEditing = useApp((s) => s.issueEditing);
  const showFlash = useApp((s) => s.showFlash);
  const drillTo = useApp((s) => s.drillTo);
  const showCoach = useApp((s) => s.showCoach);

  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { leaving, dismiss } = useDismiss(close, MENU_MS);
  useFocusTrap(panelRef);

  // The verbs: what the palette can DO, not just where it can go. Context acts on
  // the open card (so it needs one, AND the right to edit it); global acts on the
  // app. Built from data the store already holds, so the ranking in palette.ts
  // stays a pure, tested thing.
  const verbs: Verbs = useMemo(() => {
    const open = selectedId ? issues.find((i) => i.id === selectedId) : undefined;
    // Only when the card can actually be changed — not for a card you may not
    // edit (the board greys those out), and not while its full editor is open,
    // where the form holds its own copy of these fields and would overwrite a
    // quick change on Save.
    const context: Command[] =
      open && !issueEditing && can(role, 'editIssue')
        ? [
            ...statusOrderFor({ stagingEnabled: stagingOn || open?.status === 'staging' }).map((s) => ({
              kind: 'run' as const,
              id: `status:${s}`,
              hint: 'Set status',
              label: STATUS_LABELS[s],
            })),
            ...PRIORITY_ORDER.map((p) => ({
              kind: 'run' as const,
              id: `priority:${p}`,
              hint: 'Set priority',
              label: PRIORITY_LABELS[p],
            })),
            // Assign to me only when it is not already mine; Unassign only when
            // there is somebody to remove — so neither is ever a verb that does
            // nothing. (Same conditions the board's own buttons use.)
            ...(myEmail && open.assignee?.toLowerCase() !== myEmail
              ? [{ kind: 'run' as const, id: 'assign:me', hint: 'Assignment', label: 'Assign to me' }]
              : []),
            ...(open.assignee
              ? [{ kind: 'run' as const, id: 'assign:none', hint: 'Assignment', label: 'Unassign' }]
              : []),
          ]
        : [];
    const global: Command[] = [
      { kind: 'run', id: 'theme:light', hint: 'Appearance', label: 'Light' },
      { kind: 'run', id: 'theme:dark', hint: 'Appearance', label: 'Dark' },
      { kind: 'run', id: 'theme:auto', hint: 'Appearance', label: 'Match system' },
      // Every other project you can move to, found by its name. Typed-only (like
      // the rest of the global verbs) so an empty palette is not a project list.
      ...projects
        .filter((p) => p.id !== projectId)
        .map((p) => ({ kind: 'run' as const, id: `project:${p.id}`, hint: 'Switch to', label: p.name })),
      { kind: 'run', id: 'signout', hint: 'Session', label: 'Sign out' },
    ];
    return { context, global };
  }, [selectedId, issues, role, myEmail, projects, projectId, issueEditing]);

  // The "how do I…" answers for what's typed, as help Commands. The hint column
  // shows the job (its group) so the list reads as an index. In the ⌘K ('all')
  // palette these are passed to the ranker and only surface on a typed query; in
  // 'help' mode they ARE the list, and an empty query shows the whole corpus.
  const helps: Command[] = useMemo(() => howTosFor(q).map(toHelpCmd), [q]);
  // The WHOLE corpus, as the no-dead-end fallback: a help query that matches no
  // how-to falls back to this browsable index rather than an empty wall, so Help
  // never says only "nothing found" (learnability F1).
  const allHelps = useMemo<Command[]>(() => HOW_TO.map(toHelpCmd), []);
  const helpNoMatch = mode === 'help' && q.trim().length > 0 && helps.length === 0;

  const results = useMemo(
    () =>
      mode === 'help'
        ? helpNoMatch
          ? allHelps
          : helps
        : commandsFor(
            q,
            places,
            issues,
            canCreateIssues(role, source),
            verbs,
            // In the full ⌘K, only surface help once the query is a real word (3+),
            // so a single letter still jumps to a screen and the list stays about
            // where to go. The dedicated Help screen is where help leads from a
            // blank query.
            q.trim().length >= 3 ? helps : []
          ),
    [mode, helps, allHelps, helpNoMatch, q, places, issues, role, source, verbs]
  );
  const chosen = Math.min(at, Math.max(results.length - 1, 0));

  const run = (c: Command | undefined) => {
    if (!c) return;
    if (c.kind === 'view') setView(c.id);
    else if (c.kind === 'action') startCreate();
    else if (c.kind === 'issue') openIssue(c.id);
    else if (c.kind === 'help') {
      // Take them to the real control (the shared drillTo reveal glows it where a
      // reveal id exists) and leave the one next step on screen as the coach-mark.
      drillTo(c.view, c.reveal);
      showCoach(c.answer);
    } else {
      // A verb: id is "<verb>:<arg>" (or just "signout").
      const [verb, arg] = c.id.split(':');
      // The card as it stands now, so setting a value it already has writes
      // nothing — no redundant save, no "changed" line in its history.
      const open = selectedId ? issues.find((i) => i.id === selectedId) : undefined;
      if (verb === 'status' && selectedId) {
        if (open?.status !== arg) updateIssue(selectedId, { status: arg as IssueStatus });
        showFlash(`Status set to ${c.label}`);
      } else if (verb === 'priority' && selectedId) {
        if (open?.priority !== arg) updateIssue(selectedId, { priority: arg as IssuePriority });
        showFlash(`Priority set to ${c.label}`);
      } else if (verb === 'assign' && selectedId) {
        const next = arg === 'me' ? myEmail : '';
        if ((open?.assignee ?? '').toLowerCase() !== next) updateIssue(selectedId, { assignee: next });
        showFlash(arg === 'me' ? 'Assigned to you' : 'Unassigned');
      } else if (verb === 'theme') {
        setThemeMode(arg as ThemeMode);
        showFlash(`Appearance: ${c.label}`);
      } else if (verb === 'project') {
        setProject(arg);
        showFlash(`Switched to ${c.label}`);
      } else if (c.id === 'signout') signOutUser();
    }
    dismiss();
  };

  // Keeps the highlighted row in view when arrowing past the bottom of the list.
  useEffect(() => {
    listRef.current?.querySelector('[data-on="yes"]')?.scrollIntoView({ block: 'nearest' });
  }, [chosen, q]);

  const onKey = (e: React.KeyboardEvent) => {
    // The palette can sit over an open card, which has its own window-level
    // shortcuts (Escape closes it, E edits, Cmd-Enter saves). Stop the keys the
    // palette acts on from reaching them, so dismissing the palette leaves the
    // card exactly as it was rather than closing it too.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
    }
    if (e.key === 'ArrowDown') setAt(Math.min(chosen + 1, results.length - 1));
    else if (e.key === 'ArrowUp') setAt(Math.max(chosen - 1, 0));
    else if (e.key === 'Enter') run(results[chosen]);
    else if (e.key === 'Escape') dismiss();
  };

  return (
    <div className={`overlay palette-overlay ${leaving ? 'is-leaving' : ''}`} onClick={dismiss}>
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`palette ${leaving ? 'is-leaving' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'help' ? 'Help' : 'Command palette'}
      >
        <div className="palette-field">
          <Icon name="search" size={15} />
          <input
            className="palette-input"
            autoFocus
            value={q}
            placeholder={
              mode === 'help'
                ? 'Ask how to do something — e.g. “sign off”, “due date”…'
                : 'Go to a screen, find a card, or run a command…'
            }
            onChange={(e) => {
              setQ(e.target.value);
              setAt(0);
            }}
            onKeyDown={onKey}
            aria-activedescendant={results[chosen] ? `pal-${chosen}` : undefined}
          />
        </div>

        {/* A one-line "what FlightDeck is" for a first-time user, on the Help
            home before they've typed — the newcomer overview the learnability
            witness found missing (F5), somewhere findable rather than buried in
            the changelog. */}
        {mode === 'help' && !q.trim() && (
          <div className="palette-help-intro">
            FlightDeck runs IT projects end to end — plan the work, track it, get it signed off and
            report on it. Ask how to do something below.
          </div>
        )}

        {/* No dead-end: a help query that matched no how-to still gets the whole
            browsable index, under a line that says nothing matched (F1). */}
        {helpNoMatch && (
          <div className="palette-help-intro">
            Nothing matches “{q.trim()}”. Here’s everything you can ask:
          </div>
        )}

        <div className="palette-list" ref={listRef} role="listbox">
          {results.length === 0 ? (
            <div className="palette-none">
              {/* Help mode never reaches here — a non-matching help query falls back to
                  the full index (helpNoMatch), so this is the 'all'-mode empty line only. */}
              Nothing matches that.
            </div>
          ) : (
            results.map((c, n) => (
              <button
                key={`${c.kind}-${c.id}`}
                id={`pal-${n}`}
                role="option"
                aria-selected={n === chosen}
                data-on={n === chosen ? 'yes' : 'no'}
                className={`palette-row ${mode === 'help' && c.kind === 'help' ? 'palette-row--help' : ''} ${n === chosen ? 'is-on' : ''}`}
                // Pointer moves the selection rather than acting, so the mouse
                // and the keyboard never disagree about what Enter would do.
                onMouseMove={() => setAt(n)}
                onClick={() => run(c)}
              >
                <span className="palette-hint">{c.hint}</span>
                <span className="palette-label">{c.label}</span>
              </button>
            ))
          )}
        </div>

        <div className="palette-keys">
          <span>
            <b>↑↓</b> move
          </span>
          <span>
            <b>↵</b> open
          </span>
          <span>
            <b>Esc</b> close
          </span>
          {/* Two things typing a letter does not make obvious: a number (with or
              without #) looks a card up, and the palette now DOES as well as goes —
              type a status, a project, "sign out". Kept quiet, a reminder. */}
          {mode === 'help' ? (
            <span className="palette-keys-note">pick an answer to open the screen</span>
          ) : (
            <>
              <span className="palette-keys-note">
                <b>#</b> and a number finds a card
              </span>
              <span className="palette-keys-note">type an action to run it</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
